import IconArrowBack2 from "@/icons/IconArrowBack2";
import * as React from "react";
import { Link } from "react-router-dom";
import { StickyHeaderContext } from "@/context/StickyHeaderContext";
import { useNavigationTrail } from "@/hooks/useNavigationTrail";
import { Divider } from "@/components/ds";

interface Props {
  title: string;
  subTitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  /** Optional explicit back destination. When omitted, falls back automatically
   *  to the real navigation trail's previous entry (wherever the user actually
   *  came from) via goBack() — no per-page wiring required. */
  backLink?: string;
  tabs?: React.ReactNode;
}

export default function StickyRouteHeader(props: Props) {
  const { setHasStickyHeader, toggleSidebar } = React.useContext(StickyHeaderContext);
  const { trail, crossSectionBack, goBack } = useNavigationTrail();

  // Signal MainLayout synchronously (before paint) so it suppresses its standalone breadcrumb.
  React.useLayoutEffect(() => {
    setHasStickyHeader(true);
    return () => setHasStickyHeader(false);
  }, [setHasStickyHeader]);

  const px = toggleSidebar ? "px-6" : "px-16";
  const contentPad = toggleSidebar ? "p-3 sm:p-6 pt-4" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16 pt-4";

  // MainLayout already renders the breadcrumb bar above this header.
  // Show a back arrow if there is a hierarchical parent OR a cross-section referrer.
  const hasAutoBack = trail.length > 1 || !!crossSectionBack;

  return (
    <>
      <header
        className="shrink-0 sticky top-0 z-50 bg-white"
        style={{ isolation: "isolate" }}
      >
        <div className={`flex items-center justify-between gap-3 py-3 ${px}`}>
          <div className="flex min-w-0 flex-row items-center gap-2">
            {(props.backLink || hasAutoBack) && (
              <span className="shrink-0">
                {props.backLink ? (
                  <Link to={props.backLink} aria-label="Back">
                    <IconArrowBack2 />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={goBack}
                    aria-label="Back"
                    className="appearance-none bg-transparent border-0 p-0 cursor-pointer"
                  >
                    <IconArrowBack2 />
                  </button>
                )}
              </span>
            )}
            <div className="flex min-w-0 flex-col">
              <h1 className="m-0 truncate text-base font-semibold leading-snug text-zinc-800">{props.title}</h1>
              {props.subTitle && (
                <span className="mt-0.5 text-sm font-normal text-zinc-500 dark:text-zinc-300">
                  {props.subTitle}
                </span>
              )}
            </div>
          </div>
          {props.headerActions && <span className="shrink-0">{props.headerActions}</span>}
        </div>

        {props.tabs && (
          <nav aria-label="page tabs" className={`flex overflow-x-auto ${px}`}>
            {props.tabs}
          </nav>
        )}

        <Divider className="my-0" />
      </header>

      {/* Content wrapper restores the padding that <main> no longer provides */}
      <div className={contentPad}>
        {props.children}
      </div>
    </>
  );
}
